import type { RouteRecordRaw } from 'vue-router'

import { useDefinitionsRbac } from './composables/useDefinitionsRbac'
import { registerDefinitionsMessages } from './i18n'

import type { SettingsNavEntry } from '@/features/workspace-settings'
import { useLocaleStore } from '@/shared/i18n'

export interface DefinitionsSettingsRegistrar {
  registerNav: (entry: SettingsNavEntry) => void
  registerRoutes: (records: RouteRecordRaw[]) => void
}

export const definitionsSettingsRoutes: RouteRecordRaw[] = [
  {
    path: 'workspace/definitions',
    name: 'SettingsDefinitions',
    component: () => import('./components/definitions/DefinitionsPane.vue'),
    meta: {
      titleKey: 'workspaceDefinitions.definitions.title',
      subtitleKey: 'workspaceDefinitions.definitions.description',
      navKey: 'workspace.definitions',
    },
    beforeEnter: () => registerDefinitionsMessages(useLocaleStore().locale),
  },
  {
    path: 'workspace/conversions',
    name: 'SettingsConversions',
    component: () => import('./components/conversions/ConversionsPane.vue'),
    meta: {
      titleKey: 'workspaceDefinitions.conversions.title',
      subtitleKey: 'workspaceDefinitions.conversions.description',
      navKey: 'workspace.conversions',
    },
    beforeEnter: () => registerDefinitionsMessages(useLocaleStore().locale),
  },
  {
    path: 'workspace/source-medium',
    name: 'SettingsSourceMedium',
    component: () => import('./components/sourceMedium/SourceMediumPane.vue'),
    meta: {
      titleKey: 'workspaceDefinitions.sourceMedium.title',
      subtitleKey: 'workspaceDefinitions.sourceMedium.description',
      navKey: 'workspace.sourceMedium',
    },
    beforeEnter: () => registerDefinitionsMessages(useLocaleStore().locale),
  },
  {
    path: 'workspace/product',
    name: 'SettingsProduct',
    component: () => import('./components/product/ProductPane.vue'),
    meta: {
      titleKey: 'workspaceDefinitions.product.title',
      subtitleKey: 'workspaceDefinitions.product.description',
      navKey: 'workspace.product',
    },
    beforeEnter: () => registerDefinitionsMessages(useLocaleStore().locale),
  },
  {
    path: 'workspace/negative-keywords',
    name: 'SettingsNegativeKeywords',
    component: () => import('./components/keywords/NegativeKeywordsPane.vue'),
    meta: {
      titleKey: 'workspaceDefinitions.negativeKeywords.title',
      subtitleKey: 'workspaceDefinitions.negativeKeywords.description',
      navKey: 'workspace.negativeKeywords',
    },
    beforeEnter: () => registerDefinitionsMessages(useLocaleStore().locale),
  },
]

export function installDefinitionsSettings(reg: DefinitionsSettingsRegistrar): void {
  reg.registerRoutes(definitionsSettingsRoutes)
  const rbac = useDefinitionsRbac()

  const entries: SettingsNavEntry[] = [
    {
      key: 'workspace.conversions',
      labelKey: 'workspaceDefinitions.nav.conversions',
      icon: 'gear',
      group: 'workspace',
      order: 20,
      routeName: 'SettingsConversions',
      visible: () => rbac.canSeeConversions.value,
    },
    {
      key: 'workspace.definitions',
      labelKey: 'workspaceDefinitions.nav.definitions',
      icon: 'gear',
      group: 'workspace',
      order: 30,
      routeName: 'SettingsDefinitions',
      visible: () => true,
    },
    {
      key: 'workspace.sourceMedium',
      labelKey: 'workspaceDefinitions.nav.sourceMedium',
      icon: 'gear',
      group: 'workspace',
      order: 40,
      routeName: 'SettingsSourceMedium',
      visible: () => rbac.canSeeSourceMedium.value,
    },
    {
      key: 'workspace.product',
      labelKey: 'workspaceDefinitions.nav.product',
      icon: 'gear',
      group: 'workspace',
      order: 50,
      routeName: 'SettingsProduct',
      visible: () => rbac.canSeeProduct.value,
    },
    {
      key: 'workspace.negativeKeywords',
      labelKey: 'workspaceDefinitions.nav.negativeKeywords',
      icon: 'gear',
      group: 'workspace',
      order: 60,
      routeName: 'SettingsNegativeKeywords',
      visible: () => true,
    },
  ]

  for (const entry of entries) reg.registerNav(entry)
}
